import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Star, Bike, Percent, Gift, Timer, ShieldCheck } from "lucide-react";
import { api, brl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCityTheme } from "@/context/CityThemeContext";
import { TopBar, NotificationBell } from "@/components/Shell";
import { Input } from "@/components/ui/input";

const CATEGORIES = [
  { name: "Lanches", img: "https://images.unsplash.com/photo-1512152272829-e3139592d56f?w=200" },
  { name: "Pizzas", img: "https://images.pexels.com/photos/3727191/pexels-photo-3727191.jpeg?auto=compress&cs=tinysrgb&w=200" },
  { name: "Japonesa", img: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=200" },
  { name: "Brasileira", img: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200" },
  { name: "Churrasco", img: "https://images.unsplash.com/photo-1586058584825-c1e87ed735b4?w=200" },
  { name: "Açaí", img: "https://images.unsplash.com/photo-1524904237821-786af6d620ca?w=200" },
  { name: "Saudável", img: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=200" },
  { name: "Salgados", img: "https://images.unsplash.com/photo-1541592106381-b31e9677c0e5?w=200" },
  { name: "Padaria", img: "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=200" },
  { name: "Doces", img: "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200" },
];

export const StoreCard = ({ s }) => (
  <Link to={`/loja/${s.id}`} data-testid={`store-card-${s.id}`}
    className="flex gap-3 p-3 rounded-2xl border border-gray-200 hover:shadow-md hover:border-gf-orange/40 bg-white gf-press"
    style={{ transition: "box-shadow .2s ease, border-color .2s ease" }}>
    <img src={s.logo_url || s.banner_url} alt={s.name} className="w-[68px] h-[68px] rounded-xl object-cover" />
    <div className="flex-1 min-w-0">
      <p className="font-display font-bold text-gf-black truncate">{s.name}</p>
      <p className="text-xs text-gray-500">{s.category} · {s.delivery_time}</p>
      <div className="flex items-center gap-3 mt-1 text-xs">
        <span className="flex items-center gap-1 font-semibold">
          <Star size={13} fill="#F5C518" stroke="none" />
          <span className="text-gf-black">{s.rating.toFixed(1).replace(".", ",")}</span>
        </span>
        <span className="flex items-center gap-1 text-gray-600"><Bike size={13} /> {brl(s.delivery_fee)}</span>
      </div>
    </div>
  </Link>
);

export default function ConsumerHome() {
  const { user } = useAuth();
  const { cityName } = useCityTheme();
  const [stores, setStores] = useState([]);
  const [cat, setCat] = useState("Todos");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/stores", { params: { category: cat === "Todos" ? undefined : cat, q: q || undefined } })
      .then(({ data }) => setStores(data)).catch(() => {});
  }, [cat, q]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
  }, []);

  return (
    <div data-testid="consumer-home">
      <TopBar subtitle={`${cityName}/RJ · entrega em casa`} right={<NotificationBell />} />

      <div className="px-4 pt-5">
        <p className="text-sm text-gray-500">{greeting},</p>
        <h1 className="font-display text-3xl font-extrabold text-gf-black leading-tight" data-testid="consumer-greeting">
          Olá, {user?.name}!
        </h1>
        <p className="text-sm text-gray-500 mt-1">O que você vai pedir hoje?</p>

        <div className="relative mt-4">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar pratos, restaurantes..."
            className="h-12 pl-11 rounded-2xl bg-gf-gray border-transparent focus-visible:border-gf-orange" />
        </div>
      </div>

      {/* Anúncio principal: indique e ganhe */}
      <div className="px-4 mt-5">
        <Link to="/indique" data-testid="ad-referral" className="block relative overflow-hidden rounded-3xl bg-gf-black p-5 text-white gf-press">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gf-orange px-2 py-1 rounded-full">
            <Gift size={11} /> Indique e ganhe sempre
          </span>
          <h2 className="font-display text-xl font-extrabold mt-2 leading-tight max-w-[76%]">
            Um salário mínimo ou mais só indicando o Time Food
          </h2>
          <p className="text-xs text-white/75 mt-1 max-w-[76%]">
            3,5% de tudo que seu indicado consumir + 2,5% do segundo nível. Gere seu link agora.
          </p>
          <img src="https://images.unsplash.com/photo-1554672723-d42a16e533db?w=300" alt=""
            className="absolute -right-4 bottom-0 w-[110px] h-[110px] object-cover rounded-full opacity-70" />
        </Link>
      </div>

      {/* Promoções */}
      <div className="px-4 mt-3">
        <div data-testid="promo-banner" className="relative overflow-hidden rounded-3xl gf-gradient p-5 text-white">
          <div className="relative z-10 max-w-[64%]">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-black/25 px-2 py-1 rounded-full">
              <Percent size={11} /> Cupom TIME10
            </span>
            <h2 className="font-display text-lg font-extrabold mt-2 leading-tight">Promoções exclusivas só para você!</h2>
            <p className="text-xs text-white/85 mt-1">10% off usando o cupom no carrinho.</p>
          </div>
          <img src="https://images.unsplash.com/photo-1512152272829-e3139592d56f?w=400" alt="Promoção"
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-[130px] h-[130px] object-cover rounded-full border-4 border-white/25" />
        </div>
      </div>

      {/* Selos */}
      <div className="px-4 mt-4 grid grid-cols-3 gap-2" data-testid="value-props">
        {[
          { icon: Timer, t: "Entrega rápida", s: "média de 30 min" },
          { icon: ShieldCheck, t: "Código de entrega", s: "confirmação segura" },
          { icon: Gift, t: "Cashback de rede", s: "ganhe indicando" },
        ].map((v) => (
          <div key={v.t} className="rounded-2xl bg-gf-gray p-3">
            <v.icon size={16} className="text-gf-orange" />
            <p className="text-[11px] font-bold mt-1 leading-tight">{v.t}</p>
            <p className="text-[10px] text-gray-500">{v.s}</p>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <h3 className="px-4 font-display font-bold text-lg">Categorias</h3>
        <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 mt-3 pb-1">
          {["Todos", ...CATEGORIES.map((c) => c.name)].map((name) => {
            const c = CATEGORIES.find((x) => x.name === name);
            const active = cat === name;
            return (
              <button key={name} data-testid={`category-${name}`} onClick={() => setCat(name)}
                className="flex flex-col items-center gap-1.5 shrink-0 w-[68px] gf-press">
                <span className="w-14 h-14 rounded-2xl overflow-hidden border-2 flex items-center justify-center bg-gf-gray"
                  style={{ borderColor: active ? "#FF7A1A" : "transparent", transition: "border-color .2s ease" }}>
                  {c ? <img src={c.img} alt={name} className="w-full h-full object-cover" />
                     : <span className="font-display font-bold text-xs text-gf-black">Todos</span>}
                </span>
                <span className={`text-[11px] font-semibold ${active ? "text-gf-orange" : "text-gray-600"}`}>{name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 mt-6">
        <h3 className="font-display font-bold text-lg">Restaurantes perto de você</h3>
        <div className="grid gap-3 mt-3 gf-stagger" data-testid="stores-list">
          {stores.map((s) => <StoreCard key={s.id} s={s} />)}
          {stores.length === 0 && <p className="text-sm text-gray-500 py-8 text-center">Nenhum restaurante encontrado.</p>}
        </div>
      </div>
    </div>
  );
}
